import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectTaxRegistrations,
  ConnectTaxSettings,
} from '@stripe/react-connect-js';
import { Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Academy, Organization, TaxPriceBehavior } from '@court-sync/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui';
import { refreshTaxStatus, updateTaxPriceBehavior } from '../../../../services/connect';

export interface PaymentsTaxTabProps {
  academy: Academy | null;
  organization?: Organization | null;
  connectAccountId: string | null;
  stripeConnectInstance: Awaited<ReturnType<typeof loadConnectAndInitialize>> | null;
  onAcademyUpdated?: () => void;
}

function TaxPriceBehaviorOptions({
  name,
  value,
  disabled,
  onChange,
}: {
  name: string;
  value: TaxPriceBehavior;
  disabled?: boolean;
  onChange: (next: TaxPriceBehavior) => void;
}) {
  return (
    <div className="space-y-3">
      <label className="flex items-start gap-3 text-sm">
        <input
          type="radio"
          name={name}
          className="mt-1"
          checked={value === 'exclusive'}
          disabled={disabled}
          onChange={() => onChange('exclusive')}
        />
        <span>
          <span className="font-medium text-gray-900 dark:text-white">Tax added at checkout</span>
          <span className="block text-gray-600 dark:text-gray-400">
            Prices are before tax. Customers pay the listed price plus tax (common in the US).
          </span>
        </span>
      </label>
      <label className="flex items-start gap-3 text-sm">
        <input
          type="radio"
          name={name}
          className="mt-1"
          checked={value === 'inclusive'}
          disabled={disabled}
          onChange={() => onChange('inclusive')}
        />
        <span>
          <span className="font-medium text-gray-900 dark:text-white">Prices include tax</span>
          <span className="block text-gray-600 dark:text-gray-400">
            Listed prices already include tax. The checkout total stays the same and receipts show tax
            included (common for GST/VAT-inclusive pricing).
          </span>
        </span>
      </label>
    </div>
  );
}

export default function PaymentsTaxTab({
  academy,
  organization = null,
  connectAccountId,
  stripeConnectInstance,
  onAcademyUpdated,
}: PaymentsTaxTabProps) {
  const [refreshing, setRefreshing] = useState(false);
  const [taxPriceBehavior, setTaxPriceBehavior] = useState<TaxPriceBehavior>('exclusive');
  const [savingTaxBehavior, setSavingTaxBehavior] = useState(false);
  const [priceBehaviorDialogOpen, setPriceBehaviorDialogOpen] = useState(false);
  const [dialogTaxPriceBehavior, setDialogTaxPriceBehavior] = useState<TaxPriceBehavior>('exclusive');
  const lastAutoRefreshKeyRef = useRef<string | null>(null);

  const taxScope = useMemo(
    () =>
      academy?.id
        ? { academyId: academy.id }
        : organization?.id
          ? { organizationId: organization.id }
          : null,
    [academy?.id, organization?.id]
  );

  const settingsStatus = academy?.stripeTaxSettingsStatus ?? organization?.stripeTaxSettingsStatus;
  const registrationStatus =
    academy?.stripeTaxRegistrationStatus ?? organization?.stripeTaxRegistrationStatus;
  const taxSettingsActive = settingsStatus === 'active';

  const [priceBehaviorConfigured, setPriceBehaviorConfigured] = useState(
    () => taxSettingsActive
  );

  useEffect(() => {
    if (taxSettingsActive) {
      return;
    }
    setPriceBehaviorConfigured(false);
  }, [taxSettingsActive]);

  const handleRefresh = useCallback(
    async (options?: { showToast?: boolean }) => {
      if (!taxScope) return;
      const previousStatus =
        academy?.stripeTaxSettingsStatus ?? organization?.stripeTaxSettingsStatus;
      setRefreshing(true);
      try {
        const data = await refreshTaxStatus(taxScope);
        const nextBehavior =
          data.taxPriceBehavior === 'inclusive' || data.taxPriceBehavior === 'exclusive'
            ? data.taxPriceBehavior
            : taxPriceBehavior;
        if (data.taxPriceBehavior === 'inclusive' || data.taxPriceBehavior === 'exclusive') {
          setTaxPriceBehavior(data.taxPriceBehavior);
        }
        await onAcademyUpdated?.();

        const becameActive =
          previousStatus !== 'active' && data.stripeTaxSettingsStatus === 'active';
        if (becameActive) {
          setDialogTaxPriceBehavior(nextBehavior);
          setPriceBehaviorDialogOpen(true);
        }

        if (options?.showToast) {
          toast.success(
            becameActive
              ? 'Stripe Tax is active. Choose how your list prices relate to tax.'
              : 'Tax status refreshed'
          );
        }
      } catch {
        if (options?.showToast) {
          toast.error('Failed to refresh tax status');
        }
      } finally {
        setRefreshing(false);
      }
    },
    [
      taxScope,
      academy?.stripeTaxSettingsStatus,
      organization?.stripeTaxSettingsStatus,
      taxPriceBehavior,
      onAcademyUpdated,
    ]
  );

  useEffect(() => {
    if (!taxScope || !connectAccountId) {
      return;
    }
    const scopeKey = academy?.id ?? organization?.id ?? '';
    const refreshKey = `${scopeKey}:${connectAccountId}`;
    if (lastAutoRefreshKeyRef.current === refreshKey) {
      return;
    }
    lastAutoRefreshKeyRef.current = refreshKey;
    handleRefresh({ showToast: false }).catch(() => undefined);
  }, [academy?.id, organization?.id, connectAccountId, taxScope, handleRefresh]);

  useEffect(() => {
    const savedBehavior = organization?.taxPriceBehavior;
    if (savedBehavior === 'inclusive' || savedBehavior === 'exclusive') {
      setTaxPriceBehavior(savedBehavior);
    }
  }, [organization?.taxPriceBehavior]);

  const handleTaxBehaviorChange = async (next: TaxPriceBehavior) => {
    if (!taxScope || next === taxPriceBehavior) return true;
    setSavingTaxBehavior(true);
    try {
      await updateTaxPriceBehavior(taxScope, next);
      setTaxPriceBehavior(next);
      toast.success('Tax price setting updated');
      await onAcademyUpdated?.();
      return true;
    } catch {
      toast.error('Failed to update tax price setting');
      return false;
    } finally {
      setSavingTaxBehavior(false);
    }
  };

  const handleDialogSave = async () => {
    const saved =
      dialogTaxPriceBehavior === taxPriceBehavior
        ? true
        : await handleTaxBehaviorChange(dialogTaxPriceBehavior);
    if (!saved) return;
    setPriceBehaviorConfigured(true);
    setPriceBehaviorDialogOpen(false);
  };

  if (!connectAccountId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-600 dark:text-gray-400">
          Complete Stripe Connect setup on the Settings tab before configuring tax.
        </CardContent>
      </Card>
    );
  }

  const bannerMessage =
    settingsStatus === 'active'
      ? registrationStatus === 'active'
        ? 'Stripe Tax is active. Lesson, court hire, and membership payments calculate tax automatically when customers pay online.'
        : 'Tax settings are active. Add at least one tax registration below to collect tax in your jurisdictions.'
      : 'Stripe Tax setup is incomplete. Finish the business details and registrations below to enable tax on online payments.';

  const showListPriceCard = taxSettingsActive && priceBehaviorConfigured;

  return (
    <div className="space-y-6">
      <div
        className={`rounded-lg border p-4 text-sm ${
          settingsStatus === 'active'
            ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-900/20 dark:text-green-100'
            : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100'
        }`}
      >
        <p>{bannerMessage}</p>
        {!taxSettingsActive ? (
          <p className="mt-2 text-xs opacity-80">
            After tax business details are complete, refresh status and we&apos;ll ask how your list
            prices relate to tax. Until then, customers pay your listed prices with no tax added at
            checkout.
          </p>
        ) : null}
        <p className="mt-2 text-xs opacity-80">
          CourtSync does not provide tax advice. Configure tax according to your accountant&apos;s guidance.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => handleRefresh({ showToast: true })}
          disabled={refreshing || !taxScope}
        >
          {refreshing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Refreshing…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh status
            </>
          )}
        </Button>
      </div>

      {showListPriceCard ? (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">List prices and tax</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose how lesson prices, court hire rates, and membership plans entered in CourtSync relate to tax.
            </p>
          </CardHeader>
          <CardContent>
            <TaxPriceBehaviorOptions
              name="tax-price-behavior"
              value={taxPriceBehavior}
              disabled={savingTaxBehavior || !taxScope}
              onChange={handleTaxBehaviorChange}
            />
          </CardContent>
        </Card>
      ) : null}

      {!stripeConnectInstance ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading Stripe Tax…</span>
        </div>
      ) : (
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tax business details</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Head office address and default product tax category for your organization.
              </p>
            </CardHeader>
            <CardContent>
              <ConnectTaxSettings />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tax registrations</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Register the locations where your organization is registered to collect tax.
              </p>
            </CardHeader>
            <CardContent>
              <ConnectTaxRegistrations />
            </CardContent>
          </Card>
        </ConnectComponentsProvider>
      )}

      <Dialog
        open={priceBehaviorDialogOpen}
        onOpenChange={(open) => {
          if (!open && !priceBehaviorConfigured) {
            return;
          }
          setPriceBehaviorDialogOpen(open);
        }}
      >
        <DialogContent
          className="max-w-lg"
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>How do your list prices relate to tax?</DialogTitle>
            <DialogDescription>
              Stripe Tax is now active. Choose how lesson prices, court hire rates, and membership plans
              entered in CourtSync should be treated at checkout.
            </DialogDescription>
          </DialogHeader>
          <TaxPriceBehaviorOptions
            name="tax-price-behavior-dialog"
            value={dialogTaxPriceBehavior}
            disabled={savingTaxBehavior}
            onChange={setDialogTaxPriceBehavior}
          />
          <DialogFooter>
            <Button onClick={handleDialogSave} disabled={savingTaxBehavior || !taxScope}>
              {savingTaxBehavior ? 'Saving…' : 'Save preference'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
